import { useEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { isoToDisplay, parseTypedDate } from "./date-field-helpers";

// DECISION (Grant, 2026-09-30): "the date pickers throughout the OS are bad. I
// should be able to select a date from the picker but also type it in." The
// browser's <input type="date"> only takes typing one segment at a time and
// fights the open calendar popup. This is a plain text box (type it any way you
// say it) with a calendar icon at its right edge that opens the native picker.
// Value in and out is ISO "YYYY-MM-DD", same as the native control, so callers
// save what they always saved. Drawn in docs/design/date-field-mockup-v1.html.
//
// DECISION: the control is ONE <input> (plus an invisible, absolutely-placed native
// date input used only to borrow the browser's calendar popup). There is no
// wrapper element and the icon is a CSS background (.date-field in styles.css),
// so every caller's className / style / flex-grid placement keeps working
// exactly as it did for the native date input. Rejected: a wrapper with an
// absolutely-placed button (breaks any class that sizes the input itself), and
// a custom calendar widget (worse on phones than the native picker).

type PassThrough = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange" | "min" | "max" | "inputMode" | "defaultValue" | "size"
>;

export interface DateFieldProps extends PassThrough {
  /** ISO "YYYY-MM-DD", or "" for no date. */
  value: string;
  /** Called with ISO when a real date is typed or picked, "" when cleared. */
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
}

/** Width of the icon zone at the right edge that opens the calendar on click. */
const ICON_ZONE_PX = 40;

export function DateField({ value, onChange, min, max, className, style, onBlur, onClick, onKeyDown, title, disabled, id, ...rest }: DateFieldProps) {
  const [text, setText] = useState(isoToDisplay(value));
  const [bad, setBad] = useState(false);
  const textRef = useRef<HTMLInputElement | null>(null);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const lastEmitted = useRef(value);
  // Opening the calendar can blur the text box; callers that save on blur must not
  // fire with the pre-pick value, so their onBlur is muted briefly after opening.
  const muteBlurUntil = useRef(0);

  // Follow the parent when it changes the value (reset, picker, prefill), but
  // not for our own emit, or half-typed text would be overwritten.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(isoToDisplay(value));
      setBad(false);
    }
  }, [value]);

  const emit = (iso: string) => {
    lastEmitted.current = iso;
    onChange(iso);
  };
  const inRange = (iso: string) => (!min || iso >= min) && (!max || iso <= max);

  const handleText = (raw: string) => {
    setText(raw);
    if (!raw.trim()) {
      setBad(false);
      emit("");
      return;
    }
    const iso = parseTypedDate(raw);
    if (iso && inRange(iso)) {
      setBad(false);
      emit(iso);
    } else if (value) {
      // Typing over a saved date: it is no longer valid until finished.
      emit("");
    }
  };

  const openPicker = () => {
    const picker = pickerRef.current;
    const box = textRef.current;
    if (!picker || !box || disabled) return;
    muteBlurUntil.current = Date.now() + 1500;
    // Park the invisible native input under the visible box so the browser's
    // calendar popup opens next to it. DECISION (Nate, 2026-10-17: the calendar
    // opened in the top-left corner of the screen on the Contract card):
    // Chrome mis-anchors the popup of a `position: fixed` input once the page is
    // scrolled, throwing it to the window's corner. So the helper is `absolute`
    // and placed from the box's own offsets, which hold still inside whatever
    // scrolls. It sits on the box's last pixel row, not below the box: measured in
    // Chrome, a helper parked outside the box could fail to open the calendar at
    // all, while one inside it opens the calendar directly under the box.
    // Rejected: fixed + viewport coordinates (the bug).
    picker.style.left = `${box.offsetLeft}px`;
    picker.style.top = `${box.offsetTop + box.offsetHeight - 1}px`;
    if (typeof picker.showPicker === "function") picker.showPicker();
    else picker.click();
  };

  return (
    <>
      <input
        {...rest}
        id={id}
        ref={textRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        size={12}
        placeholder={rest.placeholder ?? "MM/DD/YYYY"}
        className={className ? `date-field ${className}` : "date-field"}
        value={text}
        disabled={disabled}
        title={title ?? "Type a date, or click the calendar (Alt + Down arrow)"}
        aria-invalid={bad || undefined}
        onChange={(e) => handleText(e.target.value)}
        onBlur={(e) => {
          if (text.trim()) {
            const iso = parseTypedDate(text);
            if (iso && inRange(iso)) {
              setText(isoToDisplay(iso)); // tidy "7/12/26" -> "07/12/2026"
              setBad(false);
            } else {
              setBad(true);
            }
          }
          if (Date.now() > muteBlurUntil.current) onBlur?.(e);
        }}
        onClick={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          if (e.clientX > box.right - ICON_ZONE_PX) openPicker();
          onClick?.(e);
        }}
        onKeyDown={(e) => {
          if ((e.altKey && e.key === "ArrowDown") || e.key === "F4") {
            e.preventDefault();
            openPicker();
          }
          onKeyDown?.(e);
        }}
        style={{ ...style, paddingRight: ICON_ZONE_PX, ...(bad ? { borderColor: "var(--danger, #c0392b)" } : null) }}
      />
      {/* Native picker, invisible: only its calendar popup is borrowed. */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          setText(isoToDisplay(e.target.value));
          setBad(false);
          emit(e.target.value);
        }}
        style={{ position: "absolute", left: 0, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
      {bad && (
        <span role="alert" style={{ display: "block", fontSize: 11.5, color: "var(--danger, #c0392b)", marginTop: 3 }}>
          Enter a real date like 07/12/2026.
        </span>
      )}
    </>
  );
}
